import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  message: string;
  type: NotificationType;
  duration?: number;
  title?: string;
}

interface ConfirmDialogOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  showNotification: (message: string, type: NotificationType, duration?: number) => void;
  removeNotification: (id: string) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  confirm: (options: ConfirmDialogOptions) => Promise<boolean>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// AudioContext global reutilizável (criado após primeira interação do usuário)
let globalAudioContext: AudioContext | null = null;
let audioContextInitialized = false;

// Inicializar AudioContext após interação do usuário
const initializeAudioContext = () => {
  if (audioContextInitialized || globalAudioContext) return;
  
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (AudioContextClass) {
      globalAudioContext = new AudioContextClass();
      audioContextInitialized = true;
      
      // Resumir se estiver suspenso
      if (globalAudioContext.state === 'suspended') {
        globalAudioContext.resume().catch(() => {});
      }
      
      console.log('✅ AudioContext inicializado');
    }
  } catch (error) {
    console.warn('⚠️ Erro ao inicializar AudioContext:', error);
  }
};

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    options: ConfirmDialogOptions;
    resolve: (value: boolean) => void;
  } | null>(null);

  // Inicializar AudioContext na primeira interação do usuário
  useEffect(() => {
    const handleUserInteraction = () => {
      // Inicializar AudioContext na primeira interação
      initializeAudioContext();
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('click', handleUserInteraction);
    };

    // Adicionar listeners para interação do usuário (importante para AudioContext)
    document.addEventListener('touchstart', handleUserInteraction, { once: true });
    document.addEventListener('click', handleUserInteraction, { once: true });

    return () => {
      document.removeEventListener('touchstart', handleUserInteraction);
      document.removeEventListener('click', handleUserInteraction);
    };
  }, []);

  // Reproduzir som de notificação
  const playNotificationSound = () => {
    // Tentar usar AudioContext global primeiro (já inicializado)
    if (globalAudioContext && audioContextInitialized) {
      try {
        // Garantir que o contexto está ativo
        if (globalAudioContext.state === 'suspended') {
          globalAudioContext.resume().then(() => {
            playSoundWithContext(globalAudioContext!);
          }).catch(() => {
            // Fallback para arquivo de áudio se não conseguir resumir
            playSoundWithFile();
          });
        } else {
          playSoundWithContext(globalAudioContext);
        }
        return;
      } catch (error) {
        console.warn('⚠️ Erro ao reproduzir som via AudioContext global:', error);
        // Fallback para arquivo de áudio
        playSoundWithFile();
        return;
      }
    }

    // Se não houver AudioContext global, tentar criar um novo (pode falhar sem interação)
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        const audioContext = new AudioContextClass();
        
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            playSoundWithContext(audioContext);
          }).catch(() => {
            playSoundWithFile();
          });
        } else {
          playSoundWithContext(audioContext);
        }
        return;
      }
    } catch (error) {
      console.warn('⚠️ Erro ao criar novo AudioContext:', error);
    }

    // Fallback: usar arquivo de áudio
    playSoundWithFile();
  };

  // Função auxiliar para tocar som com AudioContext
  const playSoundWithContext = (audioContext: AudioContext) => {
    try {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Som de notificação (800Hz -> 600Hz -> 800Hz)
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('⚠️ Erro ao tocar som com AudioContext:', error);
      playSoundWithFile();
    }
  };

  // Função auxiliar para tocar som com arquivo
  const playSoundWithFile = () => {
    try {
      const audio = new Audio('/notify/notification.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {
        // Ignorar erros de autoplay
      });
    } catch (error) {
      console.warn('⚠️ Erro ao reproduzir som via arquivo:', error);
    }
  };

  // Detectar se é Android
  const isAndroid = () => {
    return /Android/i.test(navigator.userAgent);
  };

  // Detectar se é mobile
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Enviar notificação desktop/admin (NÃO usa Service Worker - apenas para desktop/admin)
  // Para mobile, use o sistema mobile-push-notifications separado
  const showPushNotification = (title: string, message: string, type: NotificationType) => {
    // Verificar se o navegador suporta notificações
    if (!('Notification' in window)) {
      console.warn('⚠️ Notificações não suportadas neste navegador');
      return;
    }

    // Verificar se já temos permissão
    if (Notification.permission === 'granted') {
      try {
        const notificationIcon = '/assets/images/image.png';
        
        const notificationOptions: NotificationOptions = {
          body: message,
          icon: notificationIcon,
          badge: notificationIcon,
          tag: `desktop-notification-${Date.now()}`,
          requireInteraction: false,
          silent: false,
          data: {
            url: window.location.href,
            timestamp: Date.now(),
            type: type,
          },
        };

        // Criar notificação desktop/admin (sem Service Worker)
        const notification = new Notification(title, notificationOptions);

        console.log('✅ Notificação desktop/admin criada:', {
          title,
          message,
          type,
        });

        // Fechar automaticamente após 8 segundos
        setTimeout(() => {
          try {
            notification.close();
          } catch (closeError) {
            console.warn('⚠️ Erro ao fechar notificação:', closeError);
          }
        }, 8000);

        // Quando clicar na notificação, focar na janela
        notification.onclick = (event) => {
          try {
            event.preventDefault();
            window.focus();
            notification.close();
          } catch (clickError) {
            console.warn('⚠️ Erro ao processar clique na notificação:', clickError);
          }
        };

        return;
      } catch (error) {
        console.error('❌ Erro ao criar notificação desktop:', error);
        return;
      }
    }
  };

  const showNotification = (message: string, type: NotificationType, duration = 3000, title?: string) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const notification: Notification = { id, message, type, duration, title };

    setNotifications((prev) => [...prev, notification]);

    // Reproduzir som de notificação
    playNotificationSound();

    // Mostrar notificação push desktop/admin (apenas desktop/admin, não mobile)
    // Para mobile, o sistema mobile-push-notifications será usado separadamente
    if (!isMobile()) {
      showPushNotification(title || `BradGrifes - ${type}`, message, type);
    }

    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const success = (message: string, duration?: number, title?: string) => {
    showNotification(message, 'success', duration, title);
  };

  const error = (message: string, duration?: number, title?: string) => {
    showNotification(message, 'error', duration, title);
  };

  const warning = (message: string, duration?: number, title?: string) => {
    showNotification(message, 'warning', duration, title);
  };

  const info = (message: string, duration?: number, title?: string) => {
    showNotification(message, 'info', duration, title);
  };

  const confirm = (options: ConfirmDialogOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfirmDialog({ options, resolve });
    });
  };

  const handleConfirm = () => {
    if (confirmDialog) {
      confirmDialog.resolve(true);
      setConfirmDialog(null);
    }
  };

  const handleCancel = () => {
    if (confirmDialog) {
      confirmDialog.resolve(false);
      setConfirmDialog(null);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        showNotification,
        removeNotification,
        success,
        error,
        warning,
        info,
        confirm,
      }}
    >
      {children}
      {confirmDialog && (
        <ConfirmDialogInternal
          isOpen={true}
          title={confirmDialog.options.title}
          message={confirmDialog.options.message}
          confirmText={confirmDialog.options.confirmText}
          cancelText={confirmDialog.options.cancelText}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </NotificationContext.Provider>
  );
}

// Componente interno para o diálogo de confirmação
function ConfirmDialogInternal({
  isOpen,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onConfirm();
    }, 200);
  };

  const handleCancel = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onCancel();
    }, 200);
  };

  return (
    <div className="confirm-dialog-overlay" onClick={handleCancel}>
      <div 
        className={`confirm-dialog ${isAnimating ? 'show' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="confirm-dialog-header">
          <h3 className="confirm-dialog-title">{title}</h3>
          <button className="confirm-dialog-close" onClick={handleCancel}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="confirm-dialog-body">
          <p className="confirm-dialog-message">{message}</p>
        </div>
        <div className="confirm-dialog-actions">
          <button className="confirm-dialog-btn confirm-dialog-btn-cancel" onClick={handleCancel}>
            {cancelText}
          </button>
          <button className="confirm-dialog-btn confirm-dialog-btn-confirm" onClick={handleConfirm}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}


export function useNotification() {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}